import type { CSSProperties } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Tooltip } from '../../components/Tooltip';
import { getIconComponent } from './iconMapper';
import { useExpandableModules } from './useExpandableModules';
import "./Sidebar.css";


export interface Module {
  id: string;
  title: string;
  sidebarTitle?: string;
  icon: string;
  children?: Module[];
  badge?: string;
  visible?: boolean; // false = oculto del sidebar (MVP 2+)
}

export interface SidebarProps {
  isOpen: boolean;
  modules: Module[];
  activeModule: string;
  onModuleChange: (id: string) => void;
  theme?: 'light' | 'dark';
}

/** Variable CSS con el nivel de anidación; la sangría se calcula en Sidebar.css */
type LevelStyle = CSSProperties & { '--sb-level': number };

/**
 * Componente Sidebar para la navegación lateral
 * Muestra una navegación lateral colapsable con módulos jerárquicos.
 * Es compacto por defecto y usa la densidad cómoda desde 1536 px (ver Sidebar.css).
 */
export const Sidebar = ({
  isOpen,
  modules,
  activeModule,
  onModuleChange,
  theme = 'light'
}: SidebarProps) => {
  const { toggleModule, isModuleExpanded } = useExpandableModules();

  /**
   * Maneja el click en un módulo del sidebar
   * Si el módulo tiene hijos, lo expande/colapsa. Si no, lo activa.
   */
  const handleModuleClick = (module: Module) => {
    if (module.children && module.children.length > 0) {
      toggleModule(module.id);
    } else {
      onModuleChange(module.id);
    }
  };

  /**
   * Renderiza un módulo del sidebar de forma recursiva
   * @param module - Módulo a renderizar
   * @param level - Nivel de anidación (para indentación)
   */
  const renderModule = (module: Module, level: number = 0) => {
    const hasChildren = module.children && module.children.length > 0;
    const isExpanded = isModuleExpanded(module.id);
    const levelStyle: LevelStyle = { '--sb-level': level };

    const buttonContent = (
      <button type="button"
        className="sidebar-item"
        data-active={activeModule === module.id}
        data-level={level}
        onClick={() => handleModuleClick(module)}
        style={levelStyle}
      >
        <span className="sidebar-icon">
          {getIconComponent(module.icon)}
        </span>
        <span className="sidebar-label">
          {module.sidebarTitle || module.title}
        </span>
        {module.badge && isOpen && (
          <span className="sidebar-badge">
            {module.badge}
          </span>
        )}
        {hasChildren && isOpen && (
          <span className="sidebar-chevron">
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
        )}
        {module.badge && !isOpen && (
          <span className="sidebar-badge-dot"></span>
        )}
      </button>
    );

    return (
      <div key={module.id}>
        {!isOpen ? (
          <Tooltip text={module.sidebarTitle || module.title} position="right" className="block w-full">
            {buttonContent}
          </Tooltip>
        ) : (
          buttonContent
        )}

        {hasChildren && isExpanded && isOpen && (
          <div className="sidebar-children">
            {module.children!.map(child => renderModule(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className="sidebar-container"
      data-open={isOpen}
      data-theme={theme}
    >
      <nav className="sidebar-nav">
        {modules.filter(m => m.visible !== false).map(module => renderModule(module))}
      </nav>
    </aside>
  );
};



